import React, { useState } from 'react';

const GOLD = '#d4a853';
const DARK = '#0b1120';
const CARD = 'rgba(255,255,255,0.06)';
const BORDER = 'rgba(212,168,83,0.18)';

export default function MaintenancePage({ message, onUnlocked }) {
  const [showBypass, setShowBypass] = useState(false);
  const [bypassKey, setBypassKey]   = useState('');
  const [checking, setChecking]     = useState(false);
  const [error, setError]           = useState('');

  const submitBypass = async () => {
    if (!bypassKey.trim()) return;
    setChecking(true);
    setError('');
    try {
      const r = await fetch('/api/public/maintenance/bypass', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: bypassKey.trim() }),
      });
      const d = await r.json();
      if (d.success && d.data?.valid) {
        localStorage.setItem('sn-maint-bypass', '1');
        onUnlocked();
      } else {
        setError('Invalid access key. Please check with your administrator.');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setChecking(false);
  };

  return (
    <div style={{
      minHeight: '100vh', background: DARK,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 24, position: 'relative', overflow: 'hidden',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Decorative ambient rings */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          width:900, height:900, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(212,168,83,0.06) 0%, transparent 70%)' }}/>
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          width:600, height:600, borderRadius:'50%',
          border:'1px solid rgba(212,168,83,0.08)' }}/>
        <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          width:400, height:400, borderRadius:'50%',
          border:'1px solid rgba(212,168,83,0.06)' }}/>
        {/* Floating gold dots */}
        {[0,60,120,180,240,300].map(deg => (
          <div key={deg} style={{
            position:'absolute', top:'50%', left:'50%',
            width:4, height:4, borderRadius:'50%', background:GOLD, opacity:0.3,
            transform:`rotate(${deg}deg) translate(340px) translateY(-2px)`,
          }}/>
        ))}
        {/* Corner ornament lines */}
        <div style={{ position:'absolute', top:40, left:40, width:60, height:60,
          borderTop:`2px solid rgba(212,168,83,0.3)`, borderLeft:`2px solid rgba(212,168,83,0.3)`, borderRadius:'4px 0 0 0' }}/>
        <div style={{ position:'absolute', top:40, right:40, width:60, height:60,
          borderTop:`2px solid rgba(212,168,83,0.3)`, borderRight:`2px solid rgba(212,168,83,0.3)`, borderRadius:'0 4px 0 0' }}/>
        <div style={{ position:'absolute', bottom:40, left:40, width:60, height:60,
          borderBottom:`2px solid rgba(212,168,83,0.3)`, borderLeft:`2px solid rgba(212,168,83,0.3)`, borderRadius:'0 0 0 4px' }}/>
        <div style={{ position:'absolute', bottom:40, right:40, width:60, height:60,
          borderBottom:`2px solid rgba(212,168,83,0.3)`, borderRight:`2px solid rgba(212,168,83,0.3)`, borderRadius:'0 0 4px 0' }}/>
      </div>

      {/* Main card */}
      <div style={{ position:'relative', zIndex:1, textAlign:'center', maxWidth:520, width:'100%' }}>

        {/* Logo mark */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', marginBottom:40 }}>
          <div style={{ position:'relative', width:72, height:72 }}>
            <div style={{
              position:'absolute', inset:0, borderRadius:'50%',
              border:`2px solid rgba(212,168,83,0.15)`,
              borderTop:`2px solid ${GOLD}`,
              animation:'sn-spin 3s linear infinite',
            }}/>
            <div style={{
              position:'absolute', inset:6, borderRadius:'50%',
              border:`1px solid rgba(212,168,83,0.1)`,
              borderBottom:`1px solid ${GOLD}`,
              animation:'sn-spin 2s linear infinite reverse',
            }}/>
            <div style={{
              position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:"'Playfair Display', serif", fontWeight:700, fontSize:28,
              color:GOLD, letterSpacing:'-0.02em',
            }}>S</div>
          </div>
        </div>

        {/* Headline */}
        <div style={{
          fontFamily:"'Playfair Display', serif", fontWeight:700,
          fontSize:'clamp(28px,5vw,42px)', color:'#f0e6cc',
          lineHeight:1.15, marginBottom:16, letterSpacing:'-0.02em',
        }}>
          We'll Be Back Soon
        </div>

        {/* Decorative rule */}
        <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent:'center', marginBottom:24 }}>
          <div style={{ height:1, width:48, background:`linear-gradient(to right, transparent, ${GOLD})` }}/>
          <div style={{ width:6, height:6, borderRadius:'50%', background:GOLD, opacity:0.7 }}/>
          <div style={{ height:1, width:48, background:`linear-gradient(to left, transparent, ${GOLD})` }}/>
        </div>

        {/* Message */}
        <p style={{
          fontSize:'clamp(14px,2.5vw,16px)', color:'rgba(240,230,204,0.65)',
          lineHeight:1.8, marginBottom:40, padding:'0 8px',
        }}>
          {message || 'We are performing scheduled maintenance. We will be back shortly.'}
        </p>

        {/* Status badge */}
        <div style={{
          display:'inline-flex', alignItems:'center', gap:10,
          background:CARD, border:`1px solid ${BORDER}`,
          borderRadius:40, padding:'10px 22px', marginBottom:48,
        }}>
          <span style={{
            width:8, height:8, borderRadius:'50%', background:'#f59e0b', flexShrink:0,
            boxShadow:'0 0 6px rgba(245,158,11,0.6)',
            animation:'sn-pulse 2s ease-in-out infinite',
          }}/>
          <span style={{ fontSize:13, color:'rgba(240,230,204,0.75)', fontWeight:500, letterSpacing:'0.04em' }}>
            SYSTEM MAINTENANCE IN PROGRESS
          </span>
        </div>

        {/* Hotel icon row */}
        <div style={{ display:'flex', justifyContent:'center', gap:24, marginBottom:48, opacity:0.35 }}>
          {['🏨','🛎️','🌙','🔑','🍽️'].map((icon,i) => (
            <span key={i} style={{ fontSize:20 }}>{icon}</span>
          ))}
        </div>

        {/* Bypass section */}
        {!showBypass ? (
          <button
            onClick={() => setShowBypass(true)}
            style={{
              background:'none', border:'none', color:'rgba(212,168,83,0.4)',
              fontSize:12, cursor:'pointer', letterSpacing:'0.06em',
              padding:'4px 8px', transition:'color 0.2s',
            }}
            onMouseEnter={e => e.target.style.color='rgba(212,168,83,0.8)'}
            onMouseLeave={e => e.target.style.color='rgba(212,168,83,0.4)'}
          >
            STAFF ACCESS
          </button>
        ) : (
          <div style={{
            background:CARD, border:`1px solid ${BORDER}`,
            borderRadius:14, padding:'20px 24px',
          }}>
            <div style={{ fontSize:13, fontWeight:600, color:'rgba(240,230,204,0.8)', marginBottom:12, letterSpacing:'0.04em' }}>
              STAFF ACCESS KEY
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <input
                type="password"
                autoFocus
                value={bypassKey}
                onChange={e => setBypassKey(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitBypass()}
                placeholder="Enter access key…"
                style={{
                  flex:1, background:'rgba(0,0,0,0.3)', border:`1px solid ${BORDER}`,
                  borderRadius:8, padding:'9px 12px', color:'#f0e6cc', fontSize:13,
                  outline:'none', fontFamily:'monospace', letterSpacing:'0.05em',
                }}
              />
              <button
                onClick={submitBypass}
                disabled={checking || !bypassKey.trim()}
                style={{
                  background:GOLD, border:'none', borderRadius:8, padding:'9px 18px',
                  color:DARK, fontSize:13, fontWeight:700, cursor:'pointer',
                  opacity: checking || !bypassKey.trim() ? 0.6 : 1,
                }}
              >
                {checking ? '…' : 'Unlock'}
              </button>
            </div>
            {error && <div style={{ marginTop:10, fontSize:12, color:'#f87171' }}>{error}</div>}
            <button
              onClick={() => { setShowBypass(false); setError(''); setBypassKey(''); }}
              style={{ marginTop:10, background:'none', border:'none', color:'rgba(212,168,83,0.4)', fontSize:11, cursor:'pointer' }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes sn-spin { to { transform: rotate(360deg); } }
        @keyframes sn-pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      `}</style>
    </div>
  );
}
